/**
 * Benefit Cards Implementation for Searchanise Results
 * Inserts benefit cards at specified positions within search/collection results
 */

document.addEventListener('Searchanise.Loaded', function() {
  (function($) {
    
    // ========== DEBUG SWITCH ==========
    const DEBUG = false; // Set to false to disable logging
    // ==================================
    
    const log = (...args) => {
      if (DEBUG) console.log('[LOGEIX Benefit Cards]', ...args);
    };
    
    // Store current page number
    let currentPage = 1;
    
    log('Benefit Cards module initialized');
    
    /**
     * Seeded random number generator for consistent shuffling
     */
    function seededRandom(seed) {
      let x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    }
    
    /**
     * Shuffle array with a seed for reproducible randomness
     */
    function shuffleArrayWithSeed(array, seed) {
      const shuffled = [...array];
      let currentSeed = seed;
      for (let i = shuffled.length - 1; i > 0; i--) {
        currentSeed++;
        const j = Math.floor(seededRandom(currentSeed) * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    }
    
    /**
     * Clean and parse comma-separated data string
     */
    function cleanupDataString(str) {
      return str.split(',').map(item => item.trim()).filter(Boolean);
    }
    
    /**
     * Calculate seed based on configuration and current page
     */
    function calculateSeed(baseSeed, paginationAware, pageNumber) {
      let finalSeed;
      
      if (paginationAware) {
        // Incorporate page number into seed
        finalSeed = baseSeed + (pageNumber * 1000);
        log('Seed calculation:', {
          baseSeed,
          pageNumber,
          finalSeed,
          paginationAware: true
        });
      } else {
        finalSeed = baseSeed;
        log('Seed calculation:', {
          baseSeed,
          finalSeed,
          paginationAware: false
        });
      }
      
      return finalSeed;
    }
    
    /**
     * Get benefit cards configuration and prepare them for insertion
     */
    function getBenefitCards() {
      const $cardsContainer = $('#benefit_cards');
      if (!$cardsContainer.length) {
        log('ERROR: #benefit_cards container not found');
        return [];
      }
      
      // Parse data attributes
      const positions = cleanupDataString($cardsContainer.data('position').toString()).map(Number);
      const isRandom = $cardsContainer.data('random') === true;
      const baseSeed = parseInt($cardsContainer.data('seed')) || Date.now();
      const paginationAware = $cardsContainer.data('pagination-aware') === true;
      const requestedCards = $cardsContainer.data('cards') 
        ? cleanupDataString($cardsContainer.data('cards').toString()) 
        : [];
      
      log('Configuration loaded:', {
        positions,
        isRandom,
        baseSeed,
        paginationAware,
        currentPage,
        requestedCards
      });
      
      // Calculate final seed with pagination if enabled
      const seed = calculateSeed(baseSeed, paginationAware, currentPage);
      
      // Get available card elements
      let $availableCards = $cardsContainer.children('li.lgx-benefit-card').get();
      
      log('Available cards count:', $availableCards.length);
      
      if (!$availableCards.length || !positions.length) {
        log('WARNING: No available cards or positions');
        return [];
      }
      
      let selectedCards;
      
      // Select cards based on configuration
      if (!isRandom && requestedCards.length > 0) {
        // Use specific requested cards in order
        log('Using specific requested cards (non-random mode)');
        selectedCards = requestedCards
          .map(cardId => $availableCards.find(card => $(card).data('benefit-card') === cardId))
          .filter(Boolean);
        log('Selected cards:', selectedCards.length);
      } else {
        // Use all available cards
        selectedCards = $availableCards;
        
        if (isRandom && selectedCards.length > positions.length) {
          // Shuffle and take only needed amount
          log('Shuffling and selecting', positions.length, 'cards from', selectedCards.length);
          selectedCards = shuffleArrayWithSeed(selectedCards, seed).slice(0, positions.length);
        } else if (isRandom) {
          // Shuffle all available cards
          log('Shuffling all', selectedCards.length, 'cards');
          selectedCards = shuffleArrayWithSeed(selectedCards, seed);
        }
      }
      
      // Map cards to positions with adjustment for zero-based indexing
      const finalCards = positions.slice(0, selectedCards.length)
        .map((position, index) => ({
          id: index + 1,
          position: position,
          html: selectedCards[index].outerHTML,
          handle: $(selectedCards[index]).data('benefit-card')
        }))
        .map((card, index) => ({
          ...card,
          position: card.position - index - 1 // Adjust position as cards are inserted
        }));
      
      log('Final cards to insert:', finalCards.map(c => ({
        handle: c.handle,
        position: c.position
      })));
      
      return finalCards;
    }
    
    /**
     * Insert benefit cards into search results at specified positions
     */
    function insertBenefitCards($results) {
      log('insertBenefitCards called');
      
      const $items = $results.children('li');
      
      // Remove any previously inserted benefit cards
      const removedCount = $items.filter('.lgx-benefit-card').length;
      $items.filter('.lgx-benefit-card').remove();
      log('Removed', removedCount, 'existing benefit cards');
      
      // Get product items only
      const $productItems = $results.children('li.snize-product');
      log('Product items found:', $productItems.length);
      
      // Get configured benefit cards
      const benefitCards = getBenefitCards();
      log('Benefit cards to insert:', benefitCards.length);
      
      // Insert each benefit card at its specified position
      let insertedCount = 0;
      benefitCards.forEach(card => {
        if (card.position <= $productItems.length) {
          const targetElement = $productItems.eq(card.position - 1);
          if (targetElement.length) {
            $(card.html).insertAfter(targetElement);
            insertedCount++;
            log('Inserted card', card.handle, 'after position', card.position);
          } else {
            log('WARNING: Target element not found for position', card.position);
          }
        } else {
          log('WARNING: Position', card.position, 'exceeds product count', $productItems.length);
        }
      });
      
      log('Successfully inserted', insertedCount, 'benefit cards');
    }
    
    /**
     * Extract page number from URL
     */
    function getPageFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('page')) {
        const pageNum = parseInt(urlParams.get('page'), 10);
        log('Page number from URL:', pageNum);
        return pageNum;
      }
      log('No page parameter in URL, defaulting to 1');
      return 1;
    }
    
    /**
     * Extract page number from Searchanise data
     */
    function extractPageNumber(params) {
      log('extractPageNumber called with params:', params);
      log('params keys:', params ? Object.keys(params) : 'params is null/undefined');
      
      // Try multiple sources for page number
      let pageNum = 1;
      
      if (params && params.page) {
        pageNum = parseInt(params.page, 10);
        log('✓ Page number from params.page:', pageNum);
      } else if (params && params.startIndex && params.maxResults) {
        // Calculate page from startIndex and maxResults
        pageNum = Math.floor(params.startIndex / params.maxResults) + 1;
        log('✓ Page number calculated from startIndex/maxResults:', pageNum, {
          startIndex: params.startIndex,
          maxResults: params.maxResults
        });
      } else {
        // Fallback to URL
        pageNum = getPageFromURL();
        log('✓ Using URL fallback, page:', pageNum);
      }
      
      return pageNum || 1;
    }
    
    /**
     * Listen for Searchanise data requests to capture page number
     */
    $(document).on('Searchanise.DataRequestStart', function(event, type, params) {
      log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      log('Searchanise.DataRequestStart event fired');
      log('Event type:', type);
      log('Full params object:', JSON.stringify(params, null, 2));
      log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      
      if (type === 'results') {
        const newPage = extractPageNumber(params);
        if (newPage !== currentPage) {
          log('🔄 Page changed from', currentPage, 'to', newPage);
          currentPage = newPage;
        } else {
          log('Page unchanged:', currentPage);
        }
      }
    });
    
    /**
     * Listen for Searchanise results updates and insert benefit cards
     */
    $(document).on('Searchanise.ResultsUpdated', function(event, container) {
      log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      log('Searchanise.ResultsUpdated event fired');
      
      // Always sync with URL (most reliable source)
      const urlPage = getPageFromURL();
      if (urlPage !== currentPage) {
        log('Syncing page number from URL:', currentPage, '→', urlPage);
        currentPage = urlPage;
      }
      
      log('Current page:', currentPage);
      
      setTimeout(function() {
        insertBenefitCards($('.snize-search-results-content'));
      }, 100);
    });
    
    log('Event listeners attached successfully');
    
  })(window.Searchanise.$);
});